import React from "react";
import { Box, TableCell, Tooltip, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import * as customColors from "../../../../../theme/customColors";

export const haklaiColumnBorder = "1px solid " + customColors.lightGray;
export const haklaiNameBorder = "2px solid " + (customColors.greyAndBlue || "#b0bec5");
export const haklaiNameColumnWidth = 208;
export const haklaiSectionFrameBorder = "3px solid rgba(51, 107, 136)";

// Border properties across Haklai table cells use the logical
// `borderInlineStart` / `borderInlineEnd` pair rather than the physical
// `borderLeft` / `borderRight`. This keeps the source of truth aligned with
// the column-group pseudo-frame in `haklaiPropertyPartsStyles.js`
// (`columnGroupCell`, which already uses `border-inline-*` on its ::after),
// so every separator in the table resolves to the same physical edge under
// RTL *and* under any future LTR usage. Any override sheet that neutralizes
// these borders must also use the logical property names — see
// `propertyTableHeaderCell` / `columnGroupCell` / `groupFrameTrailingActionCell`
// in the property-parts styles.
export const HaklaiStyledHeaderCell = withStyles({
  root: {
    backgroundColor: customColors.sessionBlue,
    color: customColors.white,
    fontWeight: "bold",
    textAlign: "center",
    padding: "8px 4px",
    whiteSpace: "normal",
    lineHeight: 1.2,
    fontSize: "0.9rem",
    borderInlineStart: haklaiColumnBorder,
    borderInlineEnd: haklaiColumnBorder,
    "&.MuiTableCell-sizeSmall:last-child": {
      paddingRight: 4
    }
  }
})(TableCell);

export const HaklaiStyledNameHeaderCell = withStyles({
  root: {
    backgroundColor: customColors.sessionBlue,
    color: customColors.white,
    fontWeight: "bold",
    textAlign: "center",
    padding: "8px 6px",
    whiteSpace: "normal",
    lineHeight: 1.2,
    fontSize: "0.9rem",
    borderInlineStart: haklaiNameBorder,
    borderInlineEnd: haklaiColumnBorder,
    width: haklaiNameColumnWidth
  }
})(TableCell);

export const HaklaiStyledCell = withStyles({
  root: {
    padding: "5px 3px",
    verticalAlign: "middle",
    borderInlineStart: haklaiColumnBorder,
    "&.MuiTableCell-sizeSmall:last-child": {
      paddingRight: 3
    }
  }
})(TableCell);

// NOTE: intentionally no `borderInlineStart` here. The adjacent sale-value
// cell is the start of the money-columns group frame (see `columnGroupCell`
// in `haklaiPropertyPartsStyles.js`), which already paints a 2px border at
// this exact boundary via an `::after` pseudo using `border-inline-start`.
// Keeping a physical separator here would stack on top of that pseudo and
// render as a ~4px grey+blue "shadow" band between the name and value
// columns. The header cell (`HaklaiStyledNameHeaderCell`) still owns its
// own border because the header row has no pseudo frame.
export const HaklaiStyledNameCell = withStyles({
  root: {
    padding: "5px 3px",
    verticalAlign: "middle",
    width: haklaiNameColumnWidth
  }
})(TableCell);

export const haklaiSectionStyles = {
  section: {
    border: "1px solid rgba(0, 0, 0, 0.125)",
    boxShadow: "none",
    overflow: "visible",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box"
  },
  sectionHeader: {
    backgroundColor: customColors.lightBlue,
    color: customColors.white,
    padding: 0,
    minHeight: 50,
    display: "flex",
    alignItems: "center"
  },
  sectionContent: {
    padding: 12,
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

export const buildHaklaiSectionChromeStyles = (theme, overrides = {}) => ({
  section: {
    ...haklaiSectionStyles.section,
    ...(overrides.section || {})
  },
  sectionHeader: {
    ...haklaiSectionStyles.sectionHeader,
    ...(overrides.sectionHeader || {})
  },
  sectionHeaderButton: {
    width: "100%",
    minHeight: 50,
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    textAlign: "right",
    ...(overrides.sectionHeaderButton || {})
  },
  sectionHeaderTitle: {
    color: "inherit",
    fontWeight: 500,
    ...(overrides.sectionHeaderTitle || {})
  },
  sectionToggleIcon: {
    color: theme.palette.action.active,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginInlineStart: 8,
    fontSize: "1.45rem",
    transition: "transform 180ms ease",
    ...(overrides.sectionToggleIcon || {})
  },
  sectionToggleIconExpanded: {
    transform: "rotate(180deg)",
    ...(overrides.sectionToggleIconExpanded || {})
  },
  sectionContent: {
    ...haklaiSectionStyles.sectionContent,
    ...(overrides.sectionContent || {})
  }
});

export const haklaiLockableSectionStyles = {
  lockNotice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    width: "min(460px, 100%)",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    margin: "0 auto 12px",
    padding: "10px 12px",
    border: `1px solid ${customColors.orange}`,
    backgroundColor: "rgba(255, 247, 230, 0.96)",
    borderRadius: 8
  },
  lockNoticeText: {
    color: "#8a5a00",
    fontWeight: 500,
    lineHeight: 1.35,
    minWidth: 0,
    overflowWrap: "break-word",
    textAlign: "center",
    flex: "1 1 auto"
  },
  lockedSectionContent: {
    opacity: 0.32,
    filter: "grayscale(0.18)",
    pointerEvents: "none",
    userSelect: "none"
  }
};

export const haklaiAddPillStyles = {
  addPillButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    padding: 2,
    borderRadius: 6,
    border: "1.5px solid transparent",
    backgroundColor: "transparent",
    boxShadow: "none",
    transition: "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
    "&:hover": {
      backgroundColor: "rgba(216, 139, 4, 0.08)"
    }
  },
  addPillButtonDisabled: {
    "&.Mui-disabled": {
      opacity: 1,
      backgroundColor: "rgba(220, 227, 230, 0.12)",
      borderColor: customColors.greyAndBlue,
      boxShadow: "none"
    }
  },
  addPillButtonReady: {
    backgroundColor: customColors.orange,
    borderColor: customColors.orange,
    boxShadow: "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
    "&:hover": {
      backgroundColor: "rgb(178, 123, 6)",
      borderColor: "rgb(178, 123, 6)",
      boxShadow: "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)"
    }
  },
  addIconReady: {
    color: "#1f1f1f",
    transition: "color 260ms cubic-bezier(0.22, 1, 0.36, 1)"
  },
  addIconDisabled: {
    color: "#1f1f1f",
    opacity: 1
  }
};

// Wrapper kept at `overflow: visible` on every breakpoint so sticky
// TableHead anchors to the page (its scroll-container ancestor is the
// viewport, not this box). We explicitly avoid `overflow-x: auto` here
// because that would (a) introduce a horizontal scrollbar scoped to the
// table and (b) turn the wrapper into an independent formatting context,
// which would stop the table's intrinsic width from propagating up to
// `.inner-con` — and the "card grows with its widest child" behaviour in
// `layout.css` depends on that propagation.
export const haklaiTableWrapperBaseStyles = {
  border: haklaiColumnBorder,
  borderRadius: 4,
  overflow: "visible",
  width: "100%",
  maxWidth: "100%",
  "--haklai-nav-height": "64px",
  "--haklai-header-height": "40px"
};

export const haklaiStickyHeaderStyles = {
  position: "sticky",
  top: "var(--haklai-nav-height)",
  zIndex: 1,
  backgroundColor: customColors.sessionBlue
};

// ─── Shared field-shell primitives ──────────────────────────────────────────
// `FieldShell`, `renderFieldErrorIndicator`, and `buildFieldErrorId` are the
// building blocks for the haklai tables' per-cell error surface: a fixed
// three-slot row (error "!" · input · currency adornment) whose height never
// changes as errors come and go, plus a focusable red "!" indicator that owns
// the accessible error message.
//
// CLASS-KEYS CONTRACT. Callers pass a JSS `classes` object that MUST define
// these keys (copy from `propertyParts/haklaiPropertyPartsStyles.js` or spread
// `buildHaklaiFieldShellStyles()` from this module):
//   - fieldShell
//   - fieldShellStatusSlot
//   - fieldShellInputSlot
//   - fieldShellCurrencySlot
//   - fieldShellCurrencyText
//   - fieldShellCurrencyHidden
//   - fieldErrorIcon
//   - fieldErrorIconHidden
//   - visuallyHidden

export const joinClassNames = (...names) => names.filter(Boolean).join(" ");

export const buildFieldErrorId = (rowKey, fieldName) => (
  rowKey && fieldName ? `haklai-${rowKey}-${fieldName}-err` : undefined
);

// The indicator is a compact red "!" that owns the accessible error message:
// - Focusable when there IS an error (keyboard users can reveal the tooltip).
// - Mirrors the message into a visually-hidden span so assistive tech reads it
//   even without hovering, and the input's `aria-describedby` points at it.
// - Always rendered (hidden via visibility) so the row height never changes
//   as errors come and go.
export const renderFieldErrorIndicator = (classes, errorMsg, errorId) => {
  const hasError = Boolean(errorMsg);
  return (
    <Box className={classes.fieldShellStatusSlot}>
      <Tooltip
        title={errorMsg || ""}
        placement="top"
        disableHoverListener={!hasError}
        disableFocusListener={!hasError}
        disableTouchListener={!hasError}
      >
        <span
          role={hasError ? "img" : undefined}
          aria-label={hasError ? errorMsg : undefined}
          aria-hidden={hasError ? undefined : true}
          tabIndex={hasError ? 0 : -1}
          className={joinClassNames(
            classes.fieldErrorIcon,
            !hasError ? classes.fieldErrorIconHidden : ""
          )}
        >
          !
        </span>
      </Tooltip>
      {hasError && errorId ? (
        <span id={errorId} className={classes.visuallyHidden}>
          {errorMsg}
        </span>
      ) : null}
    </Box>
  );
};

const renderFieldCurrencySlot = (classes, hidden = false, currencyLabel = "₪") => (
  <Box className={classes.fieldShellCurrencySlot}>
    <Typography
      component="span"
      className={joinClassNames(
        classes.fieldShellCurrencyText,
        hidden ? classes.fieldShellCurrencyHidden : ""
      )}
    >
      {currencyLabel || "₪"}
    </Typography>
  </Box>
);

export const FieldShell = ({
  classes,
  shellClassName = "",
  errorMsg = "",
  errorId,
  showCurrency = false,
  currencyHidden = false,
  currencyLabel = "₪",
  children
}) => (
  <Box className={joinClassNames(classes.fieldShell, shellClassName)}>
    {renderFieldErrorIndicator(classes, errorMsg, errorId)}
    <Box className={classes.fieldShellInputSlot}>
      {children}
    </Box>
    {showCurrency ? renderFieldCurrencySlot(classes, currencyHidden, currencyLabel) : null}
  </Box>
);

// ─── Shared design tokens for validation / shell / flash ────────────────────
// These keep the expenses + property-parts tables in lockstep visually. A
// future theme pass can swap the palette in one place. Kept co-located with
// the factories that consume them so drift is self-contained.
export const HAKLAI_FIELD_SLOT_GAP = 4;
export const HAKLAI_FIELD_ERROR_SLOT_WIDTH = 14;
export const HAKLAI_FIELD_CURRENCY_SLOT_WIDTH = 14;

export const HAKLAI_ERROR_PALETTE = {
  errorTextStrong: "#d32f2f",
  errorFlashPeak: "rgba(211, 47, 47, 0.18)",
  errorFlashBase: "rgba(211, 47, 47, 0.0)",
  focusRingError: "rgba(211, 47, 47, 0.35)"
};

// Factory returning the class keys `FieldShell` + `renderFieldErrorIndicator`
// depend on. Spread into any `makeStyles` sheet that needs to host a shell —
// this is the single source of truth for row-height stability (the "!" slot
// reserves space even when hidden).
export const buildHaklaiFieldShellStyles = () => ({
  fieldShell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: HAKLAI_FIELD_SLOT_GAP,
    maxWidth: "100%",
    margin: "0 auto"
  },
  fieldShellInputSlot: {
    flex: "0 0 auto",
    minWidth: 0
  },
  fieldShellStatusSlot: {
    width: HAKLAI_FIELD_ERROR_SLOT_WIDTH,
    minWidth: HAKLAI_FIELD_ERROR_SLOT_WIDTH,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  fieldShellCurrencySlot: {
    width: HAKLAI_FIELD_CURRENCY_SLOT_WIDTH,
    minWidth: HAKLAI_FIELD_CURRENCY_SLOT_WIDTH,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  fieldShellCurrencyText: {
    fontSize: "0.72rem",
    lineHeight: 1,
    color: customColors.darkGray
  },
  fieldShellCurrencyHidden: {
    visibility: "hidden"
  },
  fieldErrorIcon: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    backgroundColor: HAKLAI_ERROR_PALETTE.errorTextStrong,
    color: "#fff",
    fontSize: "0.6rem",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    cursor: "help",
    userSelect: "none",
    outline: "none",
    "&:focus-visible": {
      boxShadow: `0 0 0 2px ${HAKLAI_ERROR_PALETTE.focusRingError}`
    }
  },
  fieldErrorIconHidden: {
    visibility: "hidden",
    pointerEvents: "none"
  },
  // Visually hidden but still accessible (WCAG technique). Paired with the
  // `aria-describedby` on each input so assistive tech reads the full error
  // sentence without the indicator having to expand.
  visuallyHidden: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0
  }
});

// Row-flash highlight used when save/analyze reveals a validation issue. The
// property-parts table uses cell `::after` pseudos for its dark-blue column
// frame, so the flash must not use a cell pseudo-element. Instead, pulse the
// cell surface and the MUI input surfaces separately; this keeps the blue
// frame stable while still tinting white/grey controls.
export const buildHaklaiRowFlashStyles = () => ({
  "@keyframes haklaiRowFlashSurface": {
    "0%": { backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashBase },
    "25%": { backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashPeak },
    "75%": { backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashPeak },
    "100%": { backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashBase }
  },
  "@keyframes haklaiRowFlashInputSurface": {
    "0%": {
      backgroundColor: "transparent",
      boxShadow: "inset 0 0 0 9999px rgba(211, 47, 47, 0)"
    },
    "25%": {
      backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashPeak,
      boxShadow: "inset 0 0 0 9999px rgba(211, 47, 47, 0.08)"
    },
    "75%": {
      backgroundColor: HAKLAI_ERROR_PALETTE.errorFlashPeak,
      boxShadow: "inset 0 0 0 9999px rgba(211, 47, 47, 0.08)"
    },
    "100%": {
      backgroundColor: "transparent",
      boxShadow: "inset 0 0 0 9999px rgba(211, 47, 47, 0)"
    }
  },
  rowFlash: {
    "& > .MuiTableCell-root:not([rowspan])": {
      animation: "$haklaiRowFlashSurface 700ms ease-in-out"
    },
    "& > .MuiTableCell-root:not([rowspan]) .MuiInputBase-root": {
      animation: "$haklaiRowFlashInputSurface 700ms ease-in-out"
    },
    "& > .MuiTableCell-root:not([rowspan]) .MuiSelect-select, & > .MuiTableCell-root:not([rowspan]) .MuiInputBase-input": {
      backgroundColor: "transparent"
    }
  }
});
